import { useMemo } from 'react'
import type { ModelEntry } from '../lib/types'
import type { LightControls } from '../scene/CoverScene'

interface AdminPanelProps {
  models: ModelEntry[]
  currentModelKey: string
  onSelectModel: (key: string) => void
  onUploadSkin: (file: File) => void
  light: LightControls
  onLightChange: (patch: Partial<LightControls>) => void
  onResetLight: () => void
}

const COVER_TYPE_ORDER = ['hardcover', 'softcover', 'snapbook'] as const
const COVER_TYPE_LABEL: Record<string, string> = {
  hardcover: 'Hardcover',
  softcover: 'Softcover',
  snapbook: 'Snap Book',
}

export function AdminPanel({
  models,
  currentModelKey,
  onSelectModel,
  onUploadSkin,
  light,
  onLightChange,
  onResetLight,
}: AdminPanelProps) {
  const grouped = useMemo(() => {
    const by: Record<string, ModelEntry[]> = {}
    for (const m of models) (by[m.coverType] ??= []).push(m)
    return by
  }, [models])

  return (
    <aside className="panel">
      <div className="panel__title">Admin controls</div>

      <label className="field">
        <span className="field__label">Model</span>
        <select
          className="field__input"
          value={currentModelKey}
          onChange={(e) => onSelectModel(e.target.value)}
        >
          {COVER_TYPE_ORDER.filter((t) => grouped[t]?.length).map((t) => (
            <optgroup key={t} label={COVER_TYPE_LABEL[t]}>
              {grouped[t].map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__label">Cover skin (PNG / JPEG)</span>
        <input
          className="field__input"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) onUploadSkin(file)
          }}
        />
      </label>

      <div className="panel__subtitle">Lighting</div>
      <Slider label="Ambient" min={0} max={2.25} step={0.05} value={light.ambient}
        onChange={(v) => onLightChange({ ambient: v })} />
      <Slider label="Key light" min={0} max={2} step={0.05} value={light.diffuse}
        onChange={(v) => onLightChange({ diffuse: v })} />
      <Slider label="Light ↔" min={-180} max={180} step={1} value={light.azimuthDeg}
        onChange={(v) => onLightChange({ azimuthDeg: v })} />
      <Slider label="Light ↕" min={-80} max={80} step={1} value={light.elevationDeg}
        onChange={(v) => onLightChange({ elevationDeg: v })} />

      <button className="btn btn--wide" onClick={onResetLight}>Reset lighting</button>
    </aside>
  )
}

function Slider({
  label,
  min,
  max,
  step,
  value,
  onChange,
}: {
  label: string
  min: number
  max: number
  step: number
  value: number
  onChange: (v: number) => void
}) {
  return (
    <label className="slider">
      <span className="slider__label">{label}</span>
      <input
        className="slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  )
}
